import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, BUTTON_TYPES, Tooltip, AtsSkeleton } from '_atoms';
import { Typography } from '_atoms/Typography';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import { ReactComponent as Sort } from 'dist/icons/arrow_ascending_descending.svg';
import { ReactComponent as Settings } from 'dist/icons/settings.svg';
import './style.scss';

interface TableHeaderSorting {
  value: string;
  label: string;
}

interface TableHeaderProps {
  title?: React.ReactNode;
  titleExtra?: React.ReactNode;
  subtitle?: React.ReactNode;
  count?: number;
  tooltipInfo?: string;
  loading?: boolean;
  sorting?: TableHeaderSorting;
  onSorting?: () => void;
  extra?: React.ReactNode;
  newLoading?: boolean;
  sticky?: boolean;
  customizable?: boolean;
  openConfigurePopupOpen?: () => void;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  title,
  titleExtra,
  subtitle,
  count,
  tooltipInfo,
  loading,
  sorting,
  onSorting,
  extra,
  newLoading,
  sticky,
  customizable,
  openConfigurePopupOpen,
}) => {
  const { t } = useTranslation();

  if (loading && !newLoading) {
    return (
      <div className="table-default__header table-default__header_loading">
        <AtsSkeleton height={36} />
      </div>
    );
  }
  return (
    <div className={cn('table-default__header', { loading }, { sticky })}>
      <div className={cn('table-info', { 'with-title-extra': titleExtra })}>
        <div className="table-title-wrapper">
          <Typography.Title2 className="title">
            {title}
            {tooltipInfo && (
              <Tooltip label={tooltipInfo}>
                <Info className="info" />
              </Tooltip>
            )}
            {!!count && <Typography.Title2 className="table-counter">({count})</Typography.Title2>}
          </Typography.Title2>
          {subtitle && <Typography.Text className="subtitle">{subtitle}</Typography.Text>}
        </div>
        {titleExtra && <div className="table-title-extra">{titleExtra}</div>}
      </div>
      <div className="table-extra">
        {sorting && (
          <Button
            className={cn('table-default__header_sorting', sorting.value)}
            onClick={onSorting}
            icon={<Sort className={sorting.value} />}
            type={BUTTON_TYPES.GHOST}
          >
            {sorting.label}
          </Button>
        )}
        {extra}
        {customizable && (
          <Button onClick={openConfigurePopupOpen} icon={<Settings />} className="configure-columns">
            {t('translation:table_header.button_customize_columns')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default TableHeader;
